/**
 * Motion tokens — Apple's fluid-interface model, expressed for Motion (framer-motion v12).
 *
 * Apple deliberately replaced the physics triplet (mass/stiffness/damping) with two
 * designer-facing parameters:
 *
 *   dampingRatio — controls overshoot. 1.0 = critically damped (no bounce). < 1.0 bounces.
 *   response     — how quickly the value reaches its target, in seconds. NOT a duration;
 *                  a spring has no fixed duration, its settle time emerges from the params.
 *
 * Motion's `{ type: 'spring', bounce, duration }` maps onto that almost directly:
 *
 *   bounce   ≈ 1 - dampingRatio
 *   duration ≈ response
 *
 * House rule: critically damped (bounce 0) everywhere by default. Reserve bounce for
 * motion the user physically drove — a flick, a throw, a drag release. Overshoot on a
 * menu that merely faded in feels wrong; overshoot on a card you flicked feels right.
 */

/** Convert Apple's (dampingRatio, response) directly into a Motion spring. */
export const appleSpring = (dampingRatio, response) => ({
  type: 'spring',
  bounce: Math.max(0, 1 - dampingRatio),
  duration: response,
});

export const spring = {
  /**
   * Pointer-down / press feedback. Must feel instantaneous — this is the
   * foundation everything else is built on; the moment lag appears, the sense
   * of directness falls off a cliff.
   */
  press: { type: 'spring', bounce: 0, duration: 0.15 },

  /** Move / reposition. Apple's PiP values: damping 1.0, response 0.4. */
  move: { type: 'spring', bounce: 0, duration: 0.4 },

  /** Snappier variant for small, frequent UI changes (hover, chip, icon nudge). */
  snap: { type: 'spring', bounce: 0, duration: 0.28 },

  /** Rotation. Apple: damping 0.8, response 0.4. */
  rotate: { type: 'spring', bounce: 0.2, duration: 0.4 },

  /** Drawer / sheet / menu panel. Apple: damping 0.8, response 0.3. */
  sheet: { type: 'spring', bounce: 0.2, duration: 0.3 },

  /**
   * Momentum landing after a flick or drag release. Bounce is earned here —
   * the gesture itself carried momentum into the animation.
   */
  flick: { type: 'spring', bounce: 0.2, duration: 0.4 },

  /**
   * Entrance reveal. No gesture preceded this, so it is critically damped —
   * graceful and non-distracting rather than springy.
   */
  reveal: { type: 'spring', bounce: 0, duration: 0.55 },

  /**
   * Smoothing applied to raw scroll progress before it drives an animation.
   *
   * Stated in the same bounce/duration form as the rest of the file so it is
   * critically damped by construction. The earlier stiffness/damping spelling
   * derived its damping from 2·√stiffness while also setting mass: 0.35, which
   * that formula ignores — the result was ζ ≈ 1.7, overdamped, and it trailed
   * the wheel rather than tracking it.
   *
   * Note that Lenis already smooths the underlying scroll position globally,
   * so this is rarely needed; stacking it on scroll-linked values adds lag.
   */
  scroll: { type: 'spring', visualDuration: 0.08, bounce: 0, restDelta: 0.0005 },
};

/**
 * Momentum projection — where a flick is *going*, not where it was released.
 *
 * This is the exponential-decay form from Apple's "Designing Fluid Interfaces"
 * sample code, not the physics-textbook v²/(2·decel). Use it to choose the
 * snap target, then hand the release velocity to the spring so there is no
 * visible seam between dragging and animating.
 *
 * @param {number} initialVelocity px/s at release
 * @param {number} decelerationRate 0.998 ≈ normal scroll feel, 0.99 = snappier
 */
export const project = (initialVelocity, decelerationRate = 0.998) =>
  ((initialVelocity / 1000) * decelerationRate) / (1 - decelerationRate);

/**
 * Rubber-banding — soft boundaries. The further past the bound, the less the
 * element follows. A hard stop reads as "frozen"; progressive resistance reads
 * as "responsive, but there's nothing more here."
 */
export const rubberband = (overshoot, dimension, constant = 0.55) =>
  (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));

/**
 * Relative velocity for spring APIs that want it normalised by remaining
 * distance. Motion takes absolute px/s via its `velocity` option, so this is
 * only needed when handing off to something that doesn't.
 */
export const relativeVelocity = (gestureVelocity, current, target) => {
  const distance = target - current;
  return distance === 0 ? 0 : gestureVelocity / distance;
};

/**
 * Easings, for the cases that genuinely must stay CSS (non-interactive,
 * non-interruptible decoration). Anything a user can touch should use a spring.
 *
 * `enter`/`exit` are inverse control points so a reversible transition retraces
 * its own path rather than taking a different route home.
 */
export const ease = {
  enter: [0.22, 1, 0.36, 1],
  exit: [0.64, 0, 0.78, 0],
  standard: [0.4, 0, 0.2, 1],
};

/** Durations for CSS-only transitions, in seconds. */
export const duration = {
  press: 0.1,
  fast: 0.2,
  base: 0.3,
  slow: 0.5,
};

/**
 * Shared viewport config for entrance reveals. `once: true` because a section
 * that re-animates every time it re-enters is distracting, and `amount` is low
 * so content is already settling by the time it is properly on screen.
 */
export const viewport = { once: true, amount: 0.2, margin: '0px 0px -10% 0px' };

/**
 * Standard entrance variants. Distance is deliberately small — large travel
 * reads as a slideshow, not as an interface.
 */
export const revealVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: spring.reveal },
};

/** Container that staggers its children's reveal. */
export const staggerVariants = (stagger = 0.06, delayChildren = 0) => ({
  hidden: {},
  visible: {
    transition: { staggerChildren: stagger, delayChildren },
  },
});

/**
 * Reduced-motion equivalents. Reduced motion does not mean *no* feedback — it
 * means a gentler, non-vestibular one. Travel and overshoot are dropped;
 * opacity, which aids comprehension, is kept.
 */
export const reducedVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2, ease: 'linear' } },
};

export default spring;
